import {
  CalendarEventIcon,
  CircleDollarIcon,
  ClipboardTextIcon,
  FileCheckIcon,
  FolderOpenIcon,
  HashtagIcon,
  LoaderIcon,
  Lock2Icon,
  NoteTextIcon,
  PaperclipIcon,
  ShareIcon,
  UserIcon,
} from '@vrplatform/icons';
import {
  type Maybe,
  day,
  ensure,
  formatCurrency,
  hasValue,
  toTitleCase,
} from '@vrplatform/utils';
import type { paidStatus_enum } from '@vrplatform/graphql';
import { type ComponentProps, type ReactNode, useMemo } from 'react';
import { Box, Group, Text, Tooltip, useMantineTheme } from '@mantine/core';
import {
  DrawerInfoCard,
  DrawerInfoCardLockBadges,
  DrawerInfoCardOwnerStatementsLinks,
} from '../../../drawers/_components';
import { getDrawerInfoCardLockReasonBadges } from '../../../drawers/_components/DrawerInfoCardLockBadges.utils';
import { InfoCardReconciliationItem } from '../../../drawers/deposit-drawer/DepositInfoCard';
import type { Expense } from '../detail/useExpenseQuery';
import { AppIconById } from '../../../components';
import { Badge, Tag } from '@vrplatform/components';
import { useTransactionAttachmentDownload } from '../../../hooks/useTransactionAttachmentDownload';
import { ExpenseDescriptionHoverCard } from './ExpenseDescriptionHoverCard';

const labelWidth = 150;
const PAGE_CARD_WIDTH = 600;
const PAGE_DESCRIPTION_LINE_CLAMP = 2;
const MAX_VISIBLE_ATTACHMENTS = 2;
const PARTIAL_LOCK_TOOLTIP =
  'The expense is only partially locked and certain fields can still be updated';

export const ExpenseInfoCard = ({
  expense,
  type,
}: {
  expense: Expense;
  type: 'drawer' | 'page';
}) => {
  const hideReconciliation = !expense.bankAccount?.id;
  const isExpenseLocked = expense.lock.expense === 'locked';
  const isPaymentLocked = expense.lock.payment === 'locked';
  const isPartiallyLocked = isExpenseLocked !== isPaymentLocked;
  const attachmentsContent = useMemo(
    () =>
      !expense.attachments.length ? null : (
        <ExpenseAttachments attachments={expense.attachments} />
      ),
    [expense.attachments]
  );
  const lockReasonBadges = useMemo(
    () =>
      getDrawerInfoCardLockReasonBadges({
        sources: [
          {
            isLocked: isExpenseLocked,
            scopeBadge:
              isPartiallyLocked && isExpenseLocked
                ? {
                    label: 'Partially',
                    tooltip: PARTIAL_LOCK_TOOLTIP,
                  }
                : undefined,
            lock: expense.lock,
          },
          {
            isLocked: isPaymentLocked,
            scopeBadge:
              isPartiallyLocked && isPaymentLocked
                ? {
                    label: 'Partially',
                    tooltip: PARTIAL_LOCK_TOOLTIP,
                  }
                : undefined,
            lock: expense.payment.lock,
          },
        ],
        fallbackBadge: {
          label: isPartiallyLocked ? 'Locked' : 'Locked',
          tooltip: expense.lock?.message,
        },
      }),
    [
      expense.lock,
      expense.payment.lock,
      isExpenseLocked,
      isPartiallyLocked,
      isPaymentLocked,
    ]
  );

  const rows = useMemo(
    () =>
      ensure<
        (ComponentProps<typeof DrawerInfoCard>['rows'][number] | undefined)[]
      >([
        type === 'drawer'
          ? undefined
          : {
              icon: ShareIcon,
              title: 'Synced from',
              text: expense.connection?.id ? (
                <Group gap="xs" wrap="nowrap">
                  <AppIconById appId={expense.connection.appId} size={20} />
                  <Text>{expense.connection.name}</Text>
                </Group>
              ) : null,
            },
        lockReasonBadges.length
          ? {
              icon: Lock2Icon,
              title: 'Locked due to',
              text: <DrawerInfoCardLockBadges badges={lockReasonBadges} />,
            }
          : undefined,
        {
          icon: LoaderIcon,
          title: 'Status',
          text:
            expense.status !== 'inactive' ? null : (
              <Badge color="orange" size="md">
                Archived
              </Badge>
            ),
        },
        {
          icon: CircleDollarIcon,
          title: 'Opening Balance',
          text: !expense.isOpeningBalance ? null : (
            <Badge color="blue" size="md">
              Opening Balance
            </Badge>
          ),
        },
        {
          icon: LoaderIcon,
          title: 'Payment',
          text: <ExpensePaidStatusBadge paidStatus={expense.payment.status} />,
        },
        {
          icon: CalendarEventIcon,
          title: 'Date',
          text: day(expense.date).format('MMM D, YYYY'),
        },
        {
          icon: CircleDollarIcon,
          title: 'Paid At',
          text: expense.payment.date
            ? day(expense.payment.date).format('MMM D, YYYY')
            : null,
        },
        {
          icon: CircleDollarIcon,
          title: 'Amount',
          text: formatCurrency(expense.centTotal / 100, expense.currency),
        },
        {
          icon: HashtagIcon,
          title: 'Bill ID',
          text: expense.uniqueRef,
        },
        {
          icon: FolderOpenIcon,
          title: 'Bank/CC Account',
          text: expense.payment.date
            ? expense.bankAccount?.title || 'Non-Trust Account'
            : null,
        },
        {
          icon: UserIcon,
          title: 'Contact',
          text: expense.contact?.id ? expense.contact?.name : null,
        },
        {
          icon: NoteTextIcon,
          title: 'Description',
          text:
            type === 'page' ? (
              expense.description ? (
                <ExpenseDescriptionHoverCard
                  description={expense.description}
                  lineClamp={PAGE_DESCRIPTION_LINE_CLAMP}
                  targetTestId="expense-description-field"
                  dropdownTestId="expense-description-field-hovercard"
                />
              ) : (
                expense.description
              )
            ) : (
              expense.description
            ),
        },
        {
          icon: PaperclipIcon,
          title: 'Attachments',
          text: attachmentsContent,
        },
        {
          icon: FileCheckIcon,
          title: 'Reconciliation',
          text: hideReconciliation ? null : (
            <InfoCardReconciliationItem bankRecordIds={expense.bankRecordIds} />
          ),
        },
        {
          icon: ClipboardTextIcon,
          title: 'Owner Statements',
          text: expense.attachedToOwnerStatementIds.length && (
            <DrawerInfoCardOwnerStatementsLinks
              ids={expense.attachedToOwnerStatementIds}
            />
          ),
        },
      ]).filter(hasValue),
    [
      attachmentsContent,
      expense.bankRecordIds[0],
      expense.centTotal,
      lockReasonBadges,
      expense.currency,
      expense.date,
      expense.description,
      expense.payment.date,
      expense.payment.status,
      expense.uniqueRef,
      expense.contact?.id,
      expense.contact?.name,
      expense.bankAccount?.title,
      expense.connection?.id,
      expense.connection?.name,
      expense.connection?.appId,
      expense.isOpeningBalance,
      expense.status,
      expense.attachedToOwnerStatementIds,
      hideReconciliation,
      isPartiallyLocked,
      type,
    ]
  );

  if (type === 'page') {
    return (
      <Box maw={PAGE_CARD_WIDTH} w="100%">
        <DrawerInfoCard rows={rows} labelWidth={labelWidth} />
      </Box>
    );
  }

  return <DrawerInfoCard rows={rows} labelWidth={labelWidth} />;
};

const ExpenseAttachments = ({
  attachments,
}: {
  attachments: Expense['attachments'];
}): ReactNode | null => {
  const theme = useMantineTheme();
  const { download } = useTransactionAttachmentDownload();

  const displayAttachments = (attachments ?? []).filter((attachment) =>
    Boolean(attachment?.filename?.trim())
  );

  if (!displayAttachments.length) return null;

  const visible = displayAttachments.slice(0, MAX_VISIBLE_ATTACHMENTS);
  const remaining = displayAttachments.length - visible.length;

  return (
    <Group gap={4} wrap="nowrap">
      {visible.map((attachment, index) => {
        const isOwnerAccessible = Boolean(attachment.isOwnerAccessible);
        const visibilityIcon = isOwnerAccessible ? 'EyeIcon' : 'EyeSlashIcon';

        return (
          <Tooltip
            key={attachment.id ?? `${attachment.filename}-${index}`}
            label={
              isOwnerAccessible
                ? 'Owner can access'
                : 'Owner does not have access'
            }
            withArrow
          >
            <Tag
              component="button"
              borderWidth={0.5}
              leftIcon={visibilityIcon}
              leftIconColor={(theme) =>
                isOwnerAccessible
                  ? theme.colors[theme.primaryColor][6]
                  : theme.colors.neutral[4]
              }
              leftIconSize={12}
              backgroundColor="transparent"
              borderColor={theme.colors.neutral[3]}
              radius={7}
              color={theme.colors.neutral[6]}
              onClick={() => {
                void download({
                  attachmentId: attachment.id,
                  filename: attachment.filename,
                });
              }}
            >
              {attachment.filename}
            </Tag>
          </Tooltip>
        );
      })}
      {remaining > 0 && (
        <Tag
          key="attachments-more"
          backgroundColor="#F5F5F5"
          borderColor="rgba(0,0,0,0.1)"
          color="#525252"
          fontSize={14}
          radius={50}
        >
          +{remaining}
        </Tag>
      )}
    </Group>
  );
};

const ExpensePaidStatusBadge = ({
  paidStatus,
}: {
  paidStatus: Maybe<paidStatus_enum>;
}) => {
  if (!paidStatus) return null;

  return (
    <Badge color={paidStatus === 'paid' ? 'green' : 'yellow'}>
      {toTitleCase(paidStatus)}
    </Badge>
  );
};
